// Weer en Gebedstijden (0025-0027, docs/DESIGN.md §8.G, §8.WE, §8.GB): de teksten van de
// instellingen, het Vandaag-scherm van de ouderapp én de zinnen die de SERVER voor de kiosk
// voorformatteert (de kiosk krijgt kant-en-klare tekst, geen sleutels). Apart bestand, niet in
// `nl` (kioskbundel). Je-vorm, korte zinnen; voor kinderen positief en concreet, nooit bang maken,
// en nooit de plannen van de ouder tegenspreken.
//
// NEUTRAAL: teksten die een gezin zonder zichtbare gebedsmodule kan zien (locatie, weer) staan in
// varianten (`geo.variants.weather|prayer|both`); de server kiest met `used_by`. In de variant
// `weather` en in `weather.*` komt het woord "gebed" nooit voor (weatherPrayer.test.ts).
//
// Gebedsnamen en het woord voor de oproep volgen de gekozen namen: Turks zoals Diyanet (İmsak,
// Güneş, Öğle, İkindi, Akşam, Yatsı; "ezan"), anders Arabisch in Latijnse letters (Fajr, Dhuhr,
// Asr, Maghrib, Isha; "adhan") met "Zonsopkomst" in het Nederlands. De server vult {call} in.
export const weatherPrayerNl = {
    geo: {
        title: 'Locatie',
        intro: 'Kies jullie postcode of woonplaats. We bewaren alleen een gebied van ongeveer een kilometer.',
        searchLabel: 'Postcode of woonplaats',
        searchPlaceholder: 'Bijvoorbeeld 3511 AA of Utrecht',
        searchButton: 'Zoeken',
        searchHint: 'Een huisnummer is niet nodig; dat sturen we ook niet mee.',
        noResults: 'Niets gevonden. Probeer een postcode of een plaatsnaam.',
        tooMany: 'Even rustig aan: probeer het over een minuut opnieuw.',
        chosen: 'Gebied: {label}',
        privacy: 'We bewaren geen straat, huisnummer of precieze plek. Gekoppelde familie, gasten en agendalinks zien dit nooit.',
        remove: 'Locatie wissen',
        removed: 'Locatie gewist.',
        /** De server kiest de variant met `used_by` (zonder zichtbare gebedsmodule: altijd `weather`). */
        variants: {
            weather: {
                settingsBody: 'Voor het weer op het bord',
                why: 'Waarom? Voor het weer op het bord. Voor niets anders.',
                removeConfirm: 'We wissen de locatie van jullie gezin en van de tablets. Het weer staat dan stil tot je een nieuwe kiest. Doorgaan?',
                askBody: 'Eén keer, voor het weer. We bewaren alleen een gebied van ongeveer een kilometer.',
            },
            prayer: {
                settingsBody: 'Voor de gebedstijden',
                why: 'Waarom? Voor de gebedstijden. Voor niets anders.',
                removeConfirm: 'We wissen de locatie van jullie gezin en van de tablets. De gebedstijden staan dan stil tot je een nieuwe kiest. Doorgaan?',
                askBody: 'Eén keer, voor de tijden op het bord. We bewaren alleen een gebied van ongeveer een kilometer.',
            },
            both: {
                settingsBody: 'Voor het weer en de gebedstijden',
                why: 'Waarom? Voor het weer op het bord en de gebedstijden. Voor niets anders.',
                removeConfirm: 'We wissen de locatie van jullie gezin en van de tablets. Weer en gebedstijden staan dan stil tot je een nieuwe kiest. Doorgaan?',
                askBody: 'Eén keer, voor het weer en de tijden op het bord. We bewaren alleen een gebied van ongeveer een kilometer.',
            },
        },
        device: {
            title: 'Locatie van deze tablet',
            household: 'Zelfde als het gezin',
            own: 'Gebruik de locatie van deze tablet',
            ownHint: 'Handig als deze tablet ergens anders staat, bijvoorbeeld in een vakantiehuis.',
            otherHome: 'Staat de tablet bij iemand anders thuis? Dan bewaren we ook van dat huis een gebied van ongeveer een kilometer. Vertel het diegene.',
            pending: 'Op de tablet verschijnt een vraag. Tik daar op "Ja, eenmalig bepalen".',
            measured: 'Gebied van deze tablet: {label}',
            far: 'Deze tablet staat ongeveer {km} km van jullie gezinsadres.',
            remeasure: 'Opnieuw bepalen',
            failed: 'De tablet kon zijn locatie niet bepalen. We gebruiken de locatie van het gezin.',
        },
        kiosk: {
            askTitle: 'Mag deze tablet zijn locatie gebruiken?',
            yes: 'Ja, eenmalig bepalen',
            no: 'Nee',
            busy: 'Even zoeken…',
            done: 'Gelukt: {label}',
            failed: 'Dat lukte niet. We gebruiken de locatie van het gezin.',
        },
        /** Ouderapp (Instellingen · Locatie, weerbouwer). */
        web: {
            choose: 'Locatie kiezen',
            change: 'Locatie wijzigen',
            none: 'Nog geen locatie gekozen.',
            area: 'Gebied: {label}',
            tablets: 'Tablets',
            tabletsNone: 'Nog geen tablets gekoppeld.',
            edit: 'Wijzigen',
            save: 'Opslaan',
            saved: 'Opgeslagen',
            cancel: 'Annuleren',
            country: 'Land',
            countryNl: 'Nederland',
            countryOther: 'Ander land',
            city: 'Plaats',
            postcode: 'Postcode',
            countryCode: 'Landcode (bijv. BE of DE)',
            searching: 'Zoeken…',
            pick: 'Kies je gebied',
            removeTitle: 'Locatie wissen?',
            unavailable: 'Zoeken lukt nu even niet. Probeer het later opnieuw.',
            requested: 'Meting aangevraagd',
            deviceSame: 'Zelfde als het gezin',
            deviceSameWith: 'Zelfde als het gezin ({label})',
        },
        credits: {
            pdok: 'Locatiegegevens: PDOK/Kadaster',
            osm: '© OpenStreetMap-bijdragers (ODbL)',
        },
    },
    weather: {
        title: 'Weer',
        settingsBody: 'Jas, laarzen of zonnebrand? Advies voor de momenten dat de kinderen de deur uit gaan',
        enabled: 'Weer tonen',
        needsLocation: 'Kies eerst een locatie, dan tonen we het weer.',
        showInBanners: 'In de vertrekmelding ("Om 8:15 naar school: regenjas en laarzen")',
        adjust: 'Aanpassen',
        morning: 'De ochtend loopt van {start} tot {end}',
        thresholdsTitle: 'Wanneer welk advies',
        thresholdsNote: 'Zon, hitte, wind en gladheid volgen RIVM en KNMI. De grenzen voor jassen en truien zijn onze eigen standaard: pas ze gerust aan.',
        thresholds: {
            sweaterBelowC: 'Vest of trui onder {value} °C (gevoel)',
            coatBelowC: 'Jas aan onder {value} °C',
            thickCoatBelowC: 'Dikke jas onder {value} °C',
            hatGlovesAtOrBelowC: 'Muts en handschoenen vanaf {value} °C en kouder',
            umbrella: 'Paraplu voorstellen bij regen en weinig wind',
            shortsFromC: 'Korte broek kan vanaf {value} °C en droog',
            waterBottleFromC: 'Drinkfles mee vanaf {value} °C',
        },
        perChild: 'Per kind',
        coldDelta: {
            label: 'Heeft het snel koud',
            value: { one: '{count} graad eerder een jas', other: '{count} graden eerder een jas' },
            warmer: { one: '{count} graad later een jas', other: '{count} graden later een jas' },
            none: 'Zoals iedereen',
            /** Uitleg boven de knoppen per kind (review e-ux r2). */
            hint: 'Heeft een kind het snel koud? Zet het getal hoger: dan krijgt het zoveel graden eerder een jas. Lager: later.',
        },
        uvClearSky: 'Zonkracht {value} (bij heldere lucht)',
        uvCloud: 'Zonkracht {value}',
        feelsLike: 'Voelt als {value} °C',
        wind: 'Wind {bft} Beaufort',
        stale: 'Weer even niet bekend',
        attribution: 'Weerdata: MET Norway',
        feel: { cold: 'koud', chilly: 'fris', mild: 'lekker', warm: 'warm', hot: 'heet' },
        /** Pictogramlabels op de kiosk (1-3 woorden, hoofdletter). Zelfde woorden als in de zinnen. */
        itemLabels: {
            warning: 'Let op',
            thunder: 'Onweer',
            very_windy: 'Harde wind',
            slippery: 'Glad',
            very_cold: 'Heel koud',
            thick_coat: 'Dikke jas',
            coat: 'Jas',
            sweater: 'Vest of trui',
            raincoat: 'Regenjas',
            boots: 'Laarzen',
            hat_gloves: 'Muts en handschoenen',
            no_umbrella: 'Capuchon op',
            umbrella: 'Paraplu',
            windy: 'Wind',
            shade: 'Uit de zon',
            sunscreen: 'Smeren',
            sun_hat: 'Pet op',
            extra_drinks: 'Veel drinken',
            water_bottle: 'Drinkfles',
            midday_shade: 'Schaduw',
            reapply: 'Nog eens smeren',
            shorts: 'Korte broek',
        },
        /** Hetzelfde in een zin (kleine letter). */
        itemWords: {
            warning: 'let op',
            thunder: 'naar binnen als het rommelt',
            very_windy: 'heel harde wind: goed vasthouden op de fiets',
            slippery: 'het kan glad zijn: rustig lopen en fietsen',
            very_cold: 'heel koud: kort buiten',
            thick_coat: 'dikke jas',
            coat: 'jas',
            sweater: 'vest of trui',
            raincoat: 'regenjas',
            boots: 'laarzen',
            hat_gloves: 'muts en handschoenen',
            no_umbrella: 'geen paraplu, capuchon op',
            umbrella: 'paraplu',
            windy: 'harde wind: pas op op de fiets',
            shade: 'uit de zon',
            sunscreen: 'smeren',
            sun_hat: 'pet op',
            extra_drinks: 'extra drinken en rustig in de schaduw spelen',
            water_bottle: 'drinkfles mee',
            midday_shade: 'tussen 12 en 3 in de schaduw spelen',
            reapply: 'om 12 uur nog eens smeren',
            shorts: 'korte broek kan',
        },
        /** Dikke jas bij regen (MomentAdvice.waterproof). */
        waterproof: 'dikke waterdichte jas',
        headline: {
            rain: 'Om {time} regent het: {items}.',
            wet: 'Vanochtend kan het nat zijn: {items}.',
            wetLater: 'Rond {time} kan het nat zijn: {items}.',
            snow: 'Om {time} sneeuwt het: {items}.',
            thunder: 'Om {time} kan het onweren: naar binnen als het rommelt.',
            cold: 'Koud buiten om {time}: {items}.',
            hot: 'Het wordt warm: {items}.',
            /** Vest bij vertrek én zon of warmte later op de dag. */
            coolThenWarm: 'Fris om {time}, later warm: {items}.',
            sun: 'Zonnig: {items}.',
            wind: 'Harde wind om {time}: pas op op de fiets.',
            nice: 'Om {time}: {feel} weer.',
            niceWith: 'Om {time}: {feel} weer, {items}.',
            warning: '{kids}',
            departure: 'Om {time} naar {title}: {items}.',
        },
        and: 'en',
        /**
         * Rustige kinderzin bij een officiële waarschuwing. Nooit "we blijven binnen" (de ouder
         * beslist over school en zwemmen); alleen bij onweer "naar binnen als het rommelt".
         */
        warningKids: {
            wind: 'Het waait heel hard. Doe je jas goed dicht en loop dicht bij papa of mama.',
            rain: 'Er valt heel veel regen. Regenjas aan en laarzen mee.',
            thunder: 'Er kan onweer komen. Als het rommelt, gaan we naar binnen.',
            snow: 'Er valt veel sneeuw. Laarzen aan en rustig lopen.',
            ice: 'Het is glad buiten. Rustig lopen, niet rennen.',
            heat: 'Het is heel warm. Veel drinken en in de schaduw spelen.',
            cold: 'Het is heel koud. Warm aankleden: muts en handschoenen.',
            fog: 'Het is mistig. Blijf dicht bij papa of mama.',
            other: 'Vandaag even goed opletten met het weer.',
        },
        warningLevel: { yellow: 'Code geel', orange: 'Code oranje', red: 'Code rood' },
        /** Ouderapp (Instellingen · Weer en Vandaag, weerbouwer). */
        web: {
            todayTitle: 'Weer vandaag',
            todayTitleAt: 'Weer vandaag · {label}',
            /** 's Avonds (vanaf 18:00) gaat de kaart over morgenochtend. */
            tomorrowTitle: 'Weer morgen',
            tomorrowTitleAt: 'Weer morgen · {label}',
            nowLabel: 'Nu',
            tomorrowLabel: 'Morgen',
            invite: 'Wil je weeradvies op het bord?',
            inviteBody: 'Jas, laarzen of zonnebrand: we zeggen het op de momenten dat de kinderen de deur uit gaan.',
            notNow: 'Niet nu',
            chooseLocation: 'Locatie kiezen',
            settings: 'Instellingen',
            showOnKiosk: 'Op het bord',
            save: 'Opslaan',
            saved: 'Opgeslagen',
            resetDefaults: 'Standaard terugzetten',
            morningStart: 'Ochtend vanaf',
            morningEnd: 'Ochtend tot',
            alsoFor: '{name}: {items}',
            everyone: 'Iedereen',
            tempNow: '{temp}°',
            staleBody: 'We halen het weer zo snel mogelijk opnieuw op.',
            childNone: 'Nog geen kinderen in het gezin.',
            /** "Weer tonen" uit terwijl alleen het weer de locatie gebruikt (review e-ux r2). */
            offTitle: 'Weer uitzetten?',
            offConfirm: 'Niets anders gebruikt de locatie. We wissen dan ook de locatie van jullie gezin en van de tablets. Doorgaan?',
            offConfirmButton: 'Uitzetten en locatie wissen',
            offNotice: 'Het weer staat uit.',
            offNoLocation: 'Het weer staat uit. Kies een locatie; daarna zet je het weer hier met één tik aan.',
            turnOn: 'Weer aanzetten',
            less: 'Minder',
            more: 'Meer',
        },
        warningSource: {
            knmi: 'Bron: KNMI',
            meteoalarm: 'Bron: KNMI via MeteoAlarm',
            fake: 'Bron: test',
        },
        /** Neerslag boven de grafiek van de widget Neerslag ({kind} is een woord uit words). */
        precip: {
            dryAll: 'Droog de komende {hours} uur.',
            nowUntil: 'Nu {kind}, droog om {end}.',
            nowLong: '{kind} de komende uren.',
            fromUntil: '{kind} vanaf {start}, droog om {end}.',
            dryUntil: 'Droog tot {start}, daarna {kind}.',
            words: {
                drizzle: 'motregen',
                rain_light: 'lichte regen',
                rain: 'regen',
                rain_heavy: 'zware regen',
                sleet: 'natte sneeuw',
                snow: 'sneeuw',
                hail: 'hagel',
                thunder: 'onweersbuien',
            },
        },
    },
    prayer: {
        title: 'Gebedstijden',
        settingsBody: 'De tijden voor jullie gebied, op het bord in de woonkamer',
        enabled: 'Gebedstijden gebruiken',
        needsLocation: 'Kies eerst een locatie. Dan rekenen we de tijden voor jullie gebied uit.',
        privacy: 'Dit hoort bij "Wat past bij ons gezin". Alleen jullie ouders zien de instellingen; gekoppelde familie en gasten nooit.',
        boardVisible: 'Let op: de tijden en de kaart staan op het bord, dus iedereen in de kamer ziet ze (ook bezoek of een oppas).',
        /** Het woord voor de oproep, per naamkeuze. */
        call: { turkish: 'ezan', arabic: 'adhan' },
        /** Met hoofdletter (keuzelabels, kaart). */
        callTitle: { turkish: 'Ezan', arabic: 'Adhan' },
        setup: {
            quickTitle: 'Gebedstijden',
            quickBody: 'We rekenen met {method}. Klopt dat met jullie moskee? Dan ben je klaar. Afstemmen en geluid per gebed kan altijd nog.',
            /** Zolang er geen ezan-opname is (geen ingebouwde, geen eigen): eerlijk zeggen wat er klinkt. */
            quickNoCall: 'Er zit nog geen {call}-opname bij. Tot jullie er zelf een uploaden, klinkt op de tablet in de woonkamer een zacht belletje.',
            /** Er is wel een ingebouwde opname, maar het belletje staat gekozen (review e r3). */
            quickTone: 'Bij "Welke opname?" staat nu het zachte belletje gekozen. Wil je de {call} horen? Kies daar de meegeleverde opname via "Meer instellen".',
            quickTimes: 'Vandaag in {place}',
            quickDone: 'Klaar',
            quickMore: 'Meer instellen',
            step1: 'Hoe rekenen we de tijden?',
            step1Body: 'Moskeeën rekenen niet allemaal hetzelfde. Kies wat jullie moskee gebruikt, of laat onze keuze staan.',
            step2: 'Stem af op je eigen moskee',
            step2Body: 'Leg het rooster van je moskee ernaast. Wijkt een tijd af, schuif hem dan. In de zomer wijken moskeeën soms een half uur of meer af: stel dan per maand in, of lees het jaarrooster in.',
            step3: 'Wat klinkt er op de tablet?',
            step3Body: 'De {call} klinkt op de tablet in de woonkamer. Andere tablets blijven stil, tenzij je ze aanzet.',
            step3BodyNoCall: 'Er is nog geen {call}-opname. Tot jullie er hieronder zelf een uploaden, klinkt bij "{callTitle}" het zachte belletje. Andere tablets blijven stil, tenzij je ze aanzet.',
            /** Wel een ingebouwde opname, maar het belletje gekozen: eerlijk zeggen waar je dat verandert. */
            step3BodyTone: 'Bij "{callTitle}" klinkt nu het zachte belletje, want dat staat gekozen bij "{step4}". Andere tablets blijven stil, tenzij je ze aanzet.',
            step4: 'Welke opname?',
            done: 'Klaar. Het volgende gebed staat nu op het bord.',
        },
        method: {
            diyanet: 'Diyanet (Turkije)',
            mwl: 'Muslim World League',
            uoif: 'UOIF (Frankrijk)',
            morocco: 'Marokko',
            jafari: 'Jafari (sjiitisch)',
            eu_joint: 'Europese gezamenlijke kalender (Diyanet · Milli Görüş)',
        },
        // Golf 6c live r1: de methode midden in een zin ("We rekenen met {method}"), met lidwoord waar
        // de naam dat nodig heeft ("met de Europese gezamenlijke kalender").
        methodInSentence: {
            diyanet: 'de kalender van Diyanet (Turkije)',
            mwl: 'Muslim World League',
            uoif: 'UOIF (Frankrijk)',
            morocco: 'de Marokkaanse methode',
            jafari: 'de Jafari-methode (sjiitisch)',
            eu_joint: 'de Europese gezamenlijke kalender (Diyanet · Milli Görüş)',
        },
        methodHint: {
            diyanet: 'De kalender voor Turkije. Turkse moskeeën in Europa gebruiken de Europese kalender hierboven.',
            mwl: 'Veel gebruikt in Europa. Een goede standaard als je het niet weet.',
            uoif: 'Latere Fajr en vroegere Isha (12°).',
            morocco: 'Wat Marokkaanse moskeeën vaak gebruiken.',
            jafari: 'Maghrib na de schemering, ongeveer een kwartier na zonsondergang.',
            eu_joint: 'Sinds 2023 dezelfde kalender voor Diyanet (ISN, DITIB) en Milli Görüş in Europa. Kies dit als jullie moskee Turks is.',
        },
        asrSchool: { standard: 'Standaard (eerste tijd)', hanafi: 'Hanafi (later op de middag)' },
        /** Golf 6c (0045): waar de Asr-keuze vandaan komt (pagina Gebedstijden). */
        asrSource: {
            title: 'Tijd van {asr}',
            madhab: 'Volgt je mazhab',
            manual: 'Zelf ingesteld',
            unset: 'Standaard',
            follow: 'Volg de mazhab weer',
            madhabLink: 'Mazhab kiezen of wijzigen',
            why: {
                hanafi: 'Jullie volgen de Hanafi-school: {asr} later op de middag.',
                other_madhab: 'Jullie volgen de {madhab}-school: {asr} op de standaardtijd.',
                calendar: 'Jullie volgen de Hanafi-school. De kalender van Diyanet en Milli Görüş noemt voor {asr} de eerste tijd (asr-ı evvel), net als jullie moskee. Liever de latere tijd (asr-ı sani)? Kies hierboven "{hanafi}".',
                no_madhab: 'Nog geen mazhab gekozen: {asr} op de standaardtijd.',
            },
            manualWhy: 'Jullie kozen dit zelf. Een andere mazhab verandert dit niet.',
        },
        /** Golf 6c: voorstel voor de bron van de tijden bij de mazhab (nooit stil toegepast). */
        madhabSuggestion: {
            body: 'Jullie volgen de Hanafi-school. Turkse moskeeën in Europa rekenen met {method}.',
            apply: 'Gebruik deze kalender',
            applied: 'We rekenen nu met {method}.',
        },
        naming: { turkish: 'Turkse namen', arabic: 'Arabische namen' },
        namingTitle: 'Namen van de gebeden',
        names: {
            turkish: {
                fajr: 'Sabah',
                sunrise: 'Güneş',
                dhuhr: 'Öğle',
                asr: 'İkindi',
                maghrib: 'Akşam',
                isha: 'Yatsı',
            },
            arabic: {
                fajr: 'Fajr',
                sunrise: 'Zonsopkomst',
                dhuhr: 'Dhuhr',
                asr: 'Asr',
                maghrib: 'Maghrib',
                isha: 'Isha',
            },
        },
        /** Naam van het gebed op de kaart (Turks: het sabahgebed begint bij İmsak). */
        cardNames: {
            turkish: { fajr: 'Sabah', dhuhr: 'Öğle', asr: 'İkindi', maghrib: 'Akşam', isha: 'Yatsı' },
            arabic: { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' },
        },
        sunriseNote: 'Zonsopkomst is het einde van de tijd voor {fajr}. Daarbij klinkt nooit iets.',
        offsets: {
            title: 'Afwijking per gebed',
            sameAllYear: 'Het hele jaar hetzelfde',
            perMonth: 'Per maand',
            month: 'In {month}',
            later: '{value} min later',
            earlier: '{value} min eerder',
            seeded: 'Afgestemd op de Diyanet-tabel van Rotterdam, alleen gecontroleerd voor september. Controleer het met je eigen moskee, zeker in de zomer.',
            /** Vandaag komt uit het ingelezen rooster: daar tellen geen afwijkingen (review e r3). */
            tableDay: 'Deze tijden komen uit het rooster van je moskee. Afwijkingen gelden alleen voor dagen die niet in het rooster staan.',
            compare: 'Vergelijk met het rooster van je moskee',
            dstCheck: 'De klok is verzet. Klopt het nog met het rooster van jullie moskee?',
            dstYes: 'Ja, klopt',
            dstAdjust: 'Aanpassen',
        },
        table: {
            title: 'Rooster van je moskee inlezen',
            body: 'Plak het jaarrooster (per regel: datum; {fajr}; zonsopkomst; {dhuhr}; {asr}; {maghrib}; {isha}). Een dag uit het rooster vervangt onze berekening.',
            read: 'Inlezen',
            result: { one: '{count} dag ingelezen', other: '{count} dagen ingelezen' },
            badLines: 'Niet te lezen: regel {lines}',
            fromTable: 'Uit het rooster van je moskee',
            remove: 'Rooster weghalen',
        },
        sound: {
            adhan: '{callTitle}',
            /** "Ezan" zonder opname: klinkt als het belletje tot er een opname is. */
            adhanPending: '{callTitle} (nog geen opname: nu het belletje)',
            /** Wel een ingebouwde opname, maar het belletje gekozen bij "Welke opname?". */
            adhanTone: '{callTitle} (nu het belletje, zie "{step4}")',
            chime: 'Zacht belletje',
            silent: 'Stil (alleen de kaart)',
        },
        yearRange: '{name}: in {month} rond {time}',
        fajrWarning: '{fajr} is vóór zonsopkomst. De {call} maakt dan het hele huis wakker.',
        /** Eén regel: bij Fajr nooit de gewone oproep (oprichtersbesluit 2026-09-25). */
        fajrNeedsRecording: 'Bij {fajr} hoort een eigen oproep (met "as-salatu khayrun min an-nawm"), dus daar klinkt nooit de gewone. Upload een eigen opname voor {fajr}, of kies het belletje.',
        quiet: {
            title: "Na de avond en 's nachts",
            body: 'Na {from} en tot {until} klinkt in plaats van de {call}:',
            chime: 'een zacht belletje',
            silent: 'niets (alleen de kaart op de woonkamer)',
            adhan: 'toch de {call}',
            hint: 'In juni valt {isha} rond middernacht en {maghrib} na tienen. Met het belletje blijft het huis rustig.',
        },
        /** Eenmalige melding na het overzetten naar de gezamenlijke kalender (0041). */
        methodNotice: 'De gebedstijden volgen nu de Europese kalender van Diyanet en Milli Görüş; in de zomer liggen {fajr} en {isha} daardoor anders.',
        methodNoticeOk: 'Oké',
        /** Eerlijk over eu_joint (gefit op de Diyanet-tabellen, niet officieel; review g6b fs r1). */
        methodApprox: 'We rekenen de gezamenlijke kalender zelf na. In Rotterdam en Amsterdam komt dat het hele jaar op 1 à 2 minuten na uit. Geen enkele gebedstijd en geen azaan op de tablet komt eerder dan de Diyanet-tabel: we ronden naar boven af, en bij {fajr} tellen we er 2 minuten bij op, van half april tot half september 10. Imsak, het begin van het vasten, is juist nooit later dan de tabel; in de Ramadan staan imsak en {fajr} allebei op het bord. Elders kan je moskee vooral van half april tot half september een paar minuten anders zijn (tot ongeveer 9). Stem af op je moskee, bijvoorbeeld met een paar minuten bij {fajr} onder Afwijking per gebed, of lees het rooster van je moskee in.',
        /** Korte zin bij de tijden (Vandaag, tablet) in de zomerweken van eu_joint. */
        methodTune: 'Zomertijden: je moskee kan een paar minuten anders zijn. Stem af op je moskee.',
        /** Opnamebibliotheek (golf 6b, DESIGN §8.GB.15). */
        library: {
            intro: 'Kies een opname per regio. Luister eerst even: het zijn echte opnames, soms met stadsgeluid of vogels.',
            regions: {
                arabic: 'Arabische stijl',
                hijaz: 'Mekka (Hijaz)',
                turkish: 'Istanbul en Turkije',
                maghreb: 'Marokko',
                egypt: 'Egypte',
                levant: 'Jordanië',
                southeast_asia: 'Maleisië',
                central_asia: 'Kazachstan',
                europe: 'Europa',
                other: 'Overig (plaats onbekend)',
            },
            places: {
                mecca: 'Mekka, Masjid al-Haram',
                istanbul: 'Istanbul (kort)',
                istanbul_fatih: 'Istanbul, Kalenderhane-moskee',
                mardin: 'Mardin, ochtend',
                patara: 'Patara, dorp, ochtend',
                casablanca: 'Casablanca, Hassan II-moskee',
                marrakech: 'Marrakech, Jemaa el-Fna',
                marrakech_medina: 'Marrakech, medina, ochtend',
                quseir: 'El Quseir, aan zee',
                amman: 'Amman, ochtend',
                kuala_lumpur: 'Kuala Lumpur, ochtend',
                shalqar: 'Shalqar',
                malmo: 'Malmö, ochtend',
                indoor: 'Vrijdag, binnen opgenomen',
            },
            option: '{place} ({year})',
            meta: '{author} · {license} · {duration}',
            fajrTitle: 'Opname voor {fajr}',
            fajrBody: 'Bij {fajr} hoort een eigen oproep, met "as-salatu khayrun min an-nawm". Kies een ochtendopname of upload een eigen opname; zonder klinkt bij {fajr} het belletje.',
            fajrNone: 'Geen (het belletje)',
            fajrUnverified: 'ochtendopname, nog niet nagekeken op de ochtendzin',
            sources: 'Bronnen en licenties',
            sourcesIntro: 'Alle opnames zijn vrij te gebruiken. Per opname staan hier de titel, de maker, de bron en de licentie. Wij haalden stilte aan begin en eind weg, maakten ze even luid en zetten ze om naar AAC.',
            by: '"{title}" van {author}, via {platform}',
            shareAlike: 'Onze bewerkte versie valt ook onder {license}.',
            report: 'Klopt een bron niet? Mail ons op support@amumin.com, dan halen we de opname weg.',
        },
        audio: {
            builtin_a: '{callTitle}, Arabische stijl',
            builtin_b: '{callTitle}, Arabische stijl (korter)',
            sunniNote: 'Deze opnames hebben de soennitische tekst.',
            shiaNote: 'Onze opnames hebben de soennitische tekst. Kies het belletje of een eigen opname.',
            tone: 'Zacht belletje (eigen geluid, anders dan de taakbelletjes)',
            own: 'Eigen opname uploaden (m4a of mp3, hooguit 5 minuten)',
            ownFajr: 'Eigen opname voor {fajr}',
            credits: 'Over de opnames',
        },
        kidsMayStop: 'Kinderen mogen de {call} stoppen',
        kidsMayStopHint: 'Uit: de Stop-knop zit bij de ouderbediening.',
        device: {
            title: 'Op welke tablets?',
            sound: '{callTitle} laten horen',
            /** Zonder ezan-opname: het belletje, dus geen "Ezan laten horen". */
            soundAny: 'Geluid bij het gebed',
            line: 'Volgende gebed in de kop',
            card: 'Kaart bij het gebed',
            cardNight: "In een kamer nooit 's nachts een kaart.",
            volume: 'Volume',
            volumeIos: 'Op een iPad zet je het volume met de knoppen aan de zijkant.',
            test: 'Test op de tablet',
            /** Neutraal: deze status staat alleen in de app, nooit in e-mail of push. */
            locked: '{name}: geluid staat uit. Tik één keer op het scherm van die tablet.',
            unsupported: '{name}: deze tablet kan geen geluid afspelen.',
            outcome: {
                played: 'Klonk om {time}',
                card_locked: 'Om {time} stil: geluid stond uit',
                card_late: 'Om {time} stil: te laat gezien',
                load_failed: 'Om {time} stil: opname niet geladen',
                skipped: 'Om {time} overgeslagen',
            },
            checklist: 'Laat het scherm aan (Automatisch slot: Nooit). In Begeleide toegang: aan-uitknop uit, volumeknoppen aan. Zet de iPad niet met de aan-uitknop in slaap: dan blijven {isha} en {fajr} stil.',
        },
        kiosk: {
            next: '{name} {time}',
            nextIn: '{name} {time} · over {left}',
            /** Aftelling in de kop (hooguit "over 7 u"). */
            inMinutes: 'over {n} min',
            inHours: 'over {n} u',
            now: 'Nu: {name}',
            cardTitle: 'Tijd voor het {name}-gebed',
            playing: '{callTitle} speelt',
            /** Het belletje speelt (geen ezan-opname, of de avondregel): nooit "Ezan speelt". */
            playingChime: 'Belletje klinkt',
            cardStop: 'Stop',
            /** De Stop van de ouder moet je vasthouden (een kind tikt hem niet per ongeluk weg). */
            cardStopHold: 'Houd vast om te stoppen',
            cardClose: 'Sluiten',
            soundLocked: 'Tik voor geluid',
            soundLockedLong: 'Geluid staat uit. Tik om aan te zetten.',
            unsupported: 'Deze tablet kan geen geluid afspelen',
            unknown: 'Gebedstijden even niet bekend',
        },
        /**
         * Golf 4 (DESIGN §8.GB.14): Ramadan op het bord. Het woord volgt de namen van het gezin
         * (Turkse namen: "Ramazan", "İmsak", "İftar"). Imsak = begin van het vasten (eu_joint: vóór
         * Sabah, het begin van het ochtendgebed), iftar = de Maghrib-tijd.
         */
        ramadan: {
            word: { turkish: 'Ramazan', arabic: 'Ramadan' },
            imsak: { turkish: 'İmsak', arabic: 'Imsak' },
            /** Begin van het ochtendgebed als dat na imsak ligt (eu_joint; integratie golf 6b live r1). */
            sabah: { turkish: 'Sabah', arabic: 'Fajr' },
            iftar: { turkish: 'İftar', arabic: 'Iftar' },
            day: '{word} · dag {n}',
            last: '{word} · laatste dag',
            countdown: 'nog {n} min',
            now: 'Tijd voor {iftar}',
            tomorrow: 'Morgen',
            eve: 'Morgen begint de {word}',
        },
        /**
         * "Gebedstijden op het bord?" (oprichtersbesluit 2026-09-25): de ene vraag in de wizard (na
         * Geloof), op Vandaag en in Instellingen, alleen als Islam bij het gezin of een lid staat. Nooit
         * vooraf gekozen.
         */
        question: {
            title: 'Gebedstijden op het bord?',
            body: 'Dan staat het volgende gebed bovenaan het bord, en klinkt op de gebedstijden de {call} op de tablet in de woonkamer.',
            body2: '{fajr} is standaard stil. Alles kun je later aanpassen.',
            yes: 'Ja, graag',
            no: 'Nee, niet nu',
            declined: 'Later aanzetten kan in Instellingen > Gebedstijden.',
            placeTitle: 'Waar wonen jullie?',
            placeBody: 'Dan rekenen we de tijden voor jullie gebied uit. Een postcode of plaats is genoeg; we bewaren alleen een gebied van ongeveer een kilometer.',
            placeOther: 'Buiten Nederland, of een tablet op een andere plek? Dat stel je in bij Instellingen > Locatie.',
            placeSave: 'Deze plek gebruiken',
            placeChosen: 'Tijden voor {label}',
            /** Golf 6c: waarom deze methode (de mazhab, nooit de keuken). */
            methodMadhab: 'We rekenen met {method}, omdat jullie de Hanafi-school volgen.',
            methodDefault: 'We rekenen met {method}.',
            change: 'Wijzigen',
            methodChoose: 'Rekenmethode',
            becauseOne: 'Omdat {name} Islam volgt.',
            becauseMany: 'Omdat {names} Islam volgen.',
            next: 'Verder',
            done: 'Klaar',
            enabled: 'Het volgende gebed staat nu op het bord.',
            renew: 'Toestemming geven en aanzetten',
            off: 'Gebedstijden staan uit. Het bord toont ze niet en er klinkt niets.',
            turnOn: 'Gebedstijden aanzetten',
            offBody: 'Liever even zonder? Dan verdwijnen de tijden van het bord en klinkt er niets. Je keuzes voor methode en geluid blijven bewaard; staat het weer ook uit, dan wissen we de locatie.',
            turnOff: 'Gebedstijden uitzetten',
            turnOffConfirm: 'Gebedstijden uitzetten? Aanzetten kan hier altijd weer.',
            turnOffYes: 'Ja, uitzetten',
            turnOffCancel: 'Toch niet',
            renewOnly: 'Toestemming geven',
            renewBody: 'De uitleg bij "Wat past bij ons gezin" is veranderd: hij noemt nu ook extra onderdelen op het bord, zoals deze gebedstijden. Geef hier opnieuw toestemming om ze te blijven gebruiken.',
        },
        /** Webapp (instellingen, gebedbouwer). */
        web: {
            consentRenewal: 'De uitleg bij "Wat past bij ons gezin" is veranderd: hij noemt nu ook extra onderdelen op het bord, zoals deze gebedstijden. Geef daar eerst opnieuw toestemming.',
            consentLink: 'Naar Wat past bij ons gezin',
            locationLink: 'Locatie kiezen',
            save: 'Opslaan',
            saved: 'Opgeslagen',
            today: 'Vandaag, {place}',
            ourTime: 'berekend',
            offset: 'afwijking',
            month: 'Maand',
            tableDays: {
                one: '{count} dag uit het rooster van je moskee',
                other: '{count} dagen uit het rooster van je moskee',
            },
            tablePlaceholder: '2026-10-01;05:40;07:30;13:35;16:45;19:15;20:50',
            listen: 'Luister',
            /** Tijdens het afspelen (review g6b ux r1: een stopknop, geen luidspreker met streep). */
            listenStop: 'Stop',
            listenPlaying: 'Speelt',
            upload: 'Bestand kiezen',
            uploaded: 'Eigen opname in gebruik',
            uploadFailed: 'Alleen een m4a- of mp3-bestand van hooguit 5 minuten en 5 MB.',
            last: 'Laatst: {items}',
            testPending: 'Test verstuurd. De uitkomst staat hier binnen een minuut.',
            /** Er is nog geen ezan-opname: de weg ernaartoe. */
            uploadCall: 'Eigen {call}-opname uploaden',
            testResult: {
                played: 'Klonk',
                card_locked: 'Stil: geluid stond uit',
                load_failed: 'Stil: opname niet geladen',
                skipped: 'Overgeslagen',
            },
            volume: 'Volume (Android, Fully Kiosk)',
            noTablets: 'Nog geen tablet gekoppeld.',
            /** "Over de opnames": herkomst van de meegeleverde opname (docs/LICENSES.md). */
            credits: {
                builtin: '{callTitle}, Arabische stijl: "{title}" van {author}, op',
                license: 'CC0 1.0 (publiek domein)',
                processing: 'Wij zetten de opname om naar AAC en MP3 (mono) en maakten hem even luid als het belletje. Verder is er niets aan veranderd.',
                tone: 'Het zachte belletje is een eigen geluid van AMUMIN.',
            },
        },
        attribution: 'Gebedstijden: eigen berekening · methode {method}',
        attributionTable: 'Gebedstijden: rooster van je moskee',
        /**
         * De islamitische (hijri) datum in de kop van het bord, alleen voor een gezin met Islam in het
         * profiel. De kiosk rekent dag, maand en jaar uit (Umm al-Qura via Intl) en vult ze hier in: de
         * maandnamen staan dus nooit in de kioskbundel en nooit "v. Chr." (bug Android Chrome, 1 okt 2026).
         */
        hijri: {
            months: [
                'Muharram',
                'Safar',
                'Rabi’ al-Awwal',
                'Rabi’ al-Akhir',
                'Jumada al-Ula',
                'Jumada al-Akhirah',
                'Rajab',
                'Sha’ban',
                'Ramadan',
                'Shawwal',
                'Dhu al-Qa’dah',
                'Dhu al-Hijjah',
            ],
            pattern: '{day} {month} {year} AH',
        },
    },
};
//# sourceMappingURL=weatherPrayer.nl.js.map